import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMediaQuery } from '../hooks/useMediaQuery';
import SiteFooter from '../components/SiteFooter';
import LogoWithBeta from '../components/LogoWithBeta';
import { submitWaitlist } from '../utils/waitlistSubmit';
import { CHAPTERS } from '../data/chapters';

export default function Waitlist() {
  const navigate = useNavigate();
  const compactNav = useMediaQuery('(max-width: 900px)');
  const [searchParams] = useSearchParams();
  const sourceParam = searchParams.get('source') || '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [email, setEmail] = useState('');
  const [chapterInterests, setChapterInterests] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [usedRemote, setUsedRemote] = useState(false);

  const sourceLabel = useMemo(() => {
    if (!sourceParam) return 'direct';
    return sourceParam.replace(/[^a-z0-9-_]/gi, '-').slice(0, 80);
  }, [sourceParam]);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (chapterInterests.length === 0) {
      setError('Please select at least one chapter.');
      return;
    }
    setBusy(true);
    const res = await submitWaitlist({
      email,
      firstName,
      lastName,
      birthday,
      chapterInterests,
      source: sourceLabel,
      referralCode: referralCode || undefined,
      note: note || undefined,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error || 'Something went wrong.');
      return;
    }
    setUsedRemote(res.mode === 'remote');
    setDone(true);
  }

  const inputStyle = {
    width: '100%',
    fontSize: 16,
    border: '1.5px solid var(--border)',
    borderRadius: 10,
    padding: '13px 16px',
    marginBottom: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };
  const chapterChoice = (id) => {
    setChapterInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--white)', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          padding: '20px 40px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 14,
            color: 'var(--pink)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          <LogoWithBeta compact={compactNav} />
          <span>← Home</span>
        </Link>
      </div>

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '40px 24px 64px', flex: 1, width: '100%', boxSizing: 'border-box' }}>
        {!done ? (
          <>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: 'var(--pink)',
                marginBottom: 10,
              }}
            >
              Waitlist
            </div>
            <h1 className="serif" style={{ fontSize: 28, color: 'var(--text)', marginBottom: 8 }}>
              Join the waitlist
            </h1>
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.65 }}>
              We&apos;re building OrivonAI now. Join the waitlist to hear when Bloom opens for you — no spam, just a note
              when we&apos;re ready.
            </p>

            <form onSubmit={onSubmit}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Email</label>
              <input
                type="email"
                autoComplete="email"
                required
                style={inputStyle}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
              <label style={{ fontSize: 12, fontWeight: 600 }}>First name</label>
              <input
                style={inputStyle}
                autoComplete="given-name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
              />
              <label style={{ fontSize: 12, fontWeight: 600 }}>Last name</label>
              <input
                style={inputStyle}
                autoComplete="family-name"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
              />
              <label style={{ fontSize: 12, fontWeight: 600 }}>Birthday (optional)</label>
              <input
                type="date"
                style={inputStyle}
                value={birthday}
                onChange={(e) => setBirthday(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 8 }}>Chapter(s) you care about most</label>
              <div style={{ ...inputStyle, marginBottom: 14, padding: '10px 12px' }}>
                {CHAPTERS.map((c) => (
                  <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>
                    <input
                      type="checkbox"
                      checked={chapterInterests.includes(c.id)}
                      onChange={() => chapterChoice(c.id)}
                    />
                    {c.name}
                  </label>
                ))}
              </div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Referral code (optional)</label>
              <input
                type="text"
                autoComplete="off"
                style={inputStyle}
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="If someone shared a code with you"
              />
              <label style={{ fontSize: 12, fontWeight: 600 }}>Comments (optional)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 88, resize: 'vertical', fontFamily: 'inherit' }}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. IVF, TTC, pregnancy…"
              />

              {error && <div style={{ fontSize: 13, color: '#b91c1c', marginBottom: 12 }}>{error}</div>}

              <button type="submit" className="btn-primary" disabled={busy} style={{ width: '100%', padding: '14px 20px' }}>
                {busy ? 'Sending…' : 'Join the waitlist'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="serif" style={{ fontSize: 26, color: 'var(--text)', marginBottom: 12 }}>
              You&apos;re on the list
            </h1>
            <p style={{ fontSize: 15, color: 'var(--muted)', lineHeight: 1.65, marginBottom: 20 }}>
              Thank you. We&apos;ll email you at <strong style={{ color: 'var(--text)' }}>{email}</strong> when we open access.
            </p>
            {!usedRemote && (
              <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16, padding: 14, background: 'var(--sand)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <strong style={{ color: 'var(--text)' }}>Heads up:</strong> email delivery isn&apos;t configured yet (add{' '}
                <code style={{ fontSize: 11 }}>REACT_APP_WAITLIST_ENDPOINT</code> to <code style={{ fontSize: 11 }}>.env</code>
                — see <code style={{ fontSize: 11 }}>.env.example</code>
                ). Entries are still stored in this browser; export with{' '}
                <code style={{ fontSize: 11 }}>JSON.stringify(window.__ORIVON_WAITLIST_EXPORT(), null, 2)</code> in the console.
              </p>
            )}
            <button type="button" className="btn-primary" onClick={() => navigate('/')} style={{ width: '100%' }}>
              Back to home
            </button>
          </>
        )}
      </div>
      <SiteFooter linkColor="var(--muted)" />
    </div>
  );
}
